/* eslint-disable no-param-reassign */
import { ArrowForwardIosSharp } from '@mui/icons-material';
import {
  Box,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Divider,
  Alert,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Member } from 'src/modules/member/models/member';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import {
  deleteFormMember,
  getFormMember,
  updateFormMember,
  deleteFormOther,
  getFormOther,
  updateFormOther,
} from 'src/shared/services/formMemberService';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FormMemberPut, FormMemberPutItem } from '../models/formMemberPut';
import { FormItemResponse, FormMemberResponse } from '../models/formMemberResponse';
import { MemberEditFamilyMessage } from './memberEditFamilyMessage';
import { MemberServerErrorSummary } from './memberServerErrorSummary';
import { MemberProfiles } from './memberProfiles';
import { MemberUpdateRequestedMessage } from './memberUpdateRequestedMessage';
import { MemberAccordionWrapper } from './memberAccordionWrapper';

type MemberEditModalProps = {
  otherContactMode: boolean;
  modalOpen: boolean;
  member: Member;
  onSave: () => void;
  onClose: () => void;
  callback: () => void;
};

export const MemberEditModal = (props: MemberEditModalProps) => {
  const { otherContactMode, modalOpen, member, onSave, onClose, callback } = props;
  const { t } = useTranslation(['member']);
  const [loading, setIsLoading] = useState(true);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [fieldsReady, setFieldsReady] = useState<boolean>(false);
  const [rowsAndColsOrder, setRowsAndColsOrder] = useState<any[]>([]);
  const [skipValidation, setSkipValidation] = useState<boolean>(false);
  const { currentUser, currentMember } = useGlobals();

  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.pageHeader'));

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const profilesTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();
  const scrollToProfilesTop = () => profilesTop.current && profilesTop.current.scrollIntoView();

  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const formMethods = useForm<FormMemberResponse>({
    defaultValues: {
      items: [{}],
    },
  });

  const { control, handleSubmit, reset } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  // Lås adressfälten om det är en familjemedlem som inte är huvudmedlem och att föreningens inställningar kräver samma adress
  const isFamilyMemberAddressLocked = useCallback(() => {
    const sameAdressMainSetting = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.SameAdress, 'false');
    return member.isFamilyMember && !member.isMainMember && sameAdressMainSetting === 'True';
  }, [currentUser, member.isFamilyMember, member.isMainMember]);

  useEffect(() => {
    const fetchFormSettings = async () => {
      setIsLoading(true);
      const result = await (otherContactMode ? getFormOther(member.member_id ?? 0) : getFormMember(member.member_id));
      if (isMounted()) {
        // const uniqueRowIds = new Set<number>();
        // // Sort by row and column, start by finding all unique rows
        // result.data.items
        //   .filter((object) => object.formKey !== null)
        //   .forEach((object) => {
        //     uniqueRowIds.add(object.formSetting.row);
        //   });

        // add default values for null
        result.data.items.map((item) => {
          const nullCheck = (val: any) => {
            if (val === null) {
              return typeof item.formValue === 'number' ? 0 : '';
            }
            return val;
          };

          return Object.assign(item, { value: nullCheck(item.formValue) });
        });

        // Step 1: Group items by groupID
        const groupMap = new Map<number, FormItemResponse[]>();

        const hiddenFormKeys = new Set(['countyAss_id', 'localAss_id']);
        result.data.items.forEach((item) => {
          if (hiddenFormKeys.has(item.formKey)) item.formSetting.type = 18;
        });
        result.data.items
          .filter((object) => object.formKey !== null)
          .forEach((object) => {
            const { groupID } = object.formSetting;
            const groupItems = groupMap.get(groupID) || [];
            groupItems.push(object);
            groupMap.set(groupID, groupItems);
          });

        const sortedFormItems: FormItemResponse[] = [];

        // Step 2: Sort within each group by row and col
        groupMap.forEach((items) => {
          const uniqueRowIds = new Set<number>();

          // Find all unique rows within the current group
          items.forEach((item) => {
            uniqueRowIds.add(item.formSetting.row);
          });

          // Sort items within the group by row and column
          Array.from(uniqueRowIds)
            .sort((a, b) => a - b)
            .forEach((rowId) => {
              const colItems = items.filter((item) => item.formSetting.row === rowId).sort((a, b) => a.formSetting.col - b.formSetting.col);
              // Kontrollera om medlemmen har tillstånd att redigera adressuppgifter
              const isAdressLocked = isFamilyMemberAddressLocked();
              colItems.forEach((child) => {
                // Lås adressuppgifter för familjmedlemmar utan behörighet
                if (
                  isAdressLocked &&
                  ['address', 'addressOther', 'postNr', 'postAddress', 'country', 'countyCode', 'municipalityCode'].some((ar) => ar === child.formKey)
                ) {
                  child.formSetting.locked = true;
                  child.formSetting.errorMessage = t('member:lockedFamilyMember');
                }
                sortedFormItems.push(child);
              });
            });
        });

        interface ColumnOrder {
          cols: string[];
        }

        // Step 3: Prepare the rows and columns structure for the form
        const mappedFormItems: ColumnOrder[] = [];
        let rowIndex = 0;
        let previousRow = 0;

        sortedFormItems.forEach((item) => {
          const { row } = item.formSetting;

          if (row !== previousRow) {
            rowIndex += 1;
          }

          const colItems = sortedFormItems.filter((itm) => itm.formSetting.row === row).map((itm) => itm.formKey);

          mappedFormItems[rowIndex] = { cols: colItems };
          previousRow = row;
        });

        setRowsAndColsOrder(mappedFormItems);
        reset({ items: sortedFormItems });
        setFieldsReady(true);
        setIsLoading(false);
      }
    };

    if (member) fetchFormSettings();
  }, [member, reset, isMounted, t, isFamilyMemberAddressLocked]);

  const saveFormData = React.useCallback(
    async (data: FormMemberPut) => {
      try {
        await (otherContactMode ? updateFormOther(data) : updateFormMember(data));
        if (isMounted()) {
          toast.success(<>{t('member:editMember.saveSuccessMessage')}</>);
          setServerErrors([]);
          onSave();
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      }
    },
    [isMounted, t, onSave],
  );

  const onSubmit = React.useCallback(
    async (data: FormMemberResponse) => {
      ConsoleHelper.log('onSubmit', data);

      const formMemberPutItems: FormMemberPutItem[] = [];

      data.items.forEach((item) => {
        let formValue = Array.isArray(item.value) ? item.value.join(',') : item.value;
        if (item.formKey === 'admDate') {
          formValue = DateHelper.getFSDate(item.value);
        }
        formMemberPutItems.push({
          formKey: item.formKey,
          formValue,
        });
      });

      const formData: FormMemberPut = {
        memberID: member.member_id || 0,
        skipValidation,
        items: formMemberPutItems,
      };

      ConsoleHelper.log('formData', formData);
      // await trigger();
      try {
        await saveFormData(formData);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e, 'error');
      }
    },
    [member.member_id, skipValidation, saveFormData, isMounted],
  );

  const onDeleteMember = async () => {
    setConfirmDeleteOpen(false);
    try {
      await (otherContactMode ? deleteFormOther(member.member_id ?? 0) : deleteFormMember(member.member_id ?? 0));
      if (isMounted()) {
        onClose();
        navigate(otherContactMode ? RoutesEnum.MemberOtherContact : RoutesEnum.Members);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log('ERR delete member', e);
    }
  };

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleSkipValidation = () => {
    setSkipValidation(true);
  };

  // Posta på nytt om man som authority >= 4 satt skipValidation till true
  useEffect(() => {
    if (skipValidation) handleSubmit(onSubmit)();
  }, [skipValidation, handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={
        otherContactMode
          ? `${t('member:editMember.otherContactHeader', { label: otherLabel })}`
          : `${t('member:editMember.header')} #${member.memberNr}`
      }
      dialogSubTitle={`${member.firstName} ${member.lastName}`}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box sx={{ position: 'relative', minHeight: '20rem' }} ref={modalTop}>
          <MemberServerErrorSummary
            serverErrors={serverErrors}
            isMember={!!currentMember}
            overridePermission={PermissionEnum.MemberUpdateOverride}
            onClickOverride={() => handleSkipValidation()}
          />
          <MemberEditFamilyMessage member={member} />
          <LoadingOverlay loading={loading} />
          {!loading && member && currentMember && member.memberUpdate_id !== null && (
            <MemberUpdateRequestedMessage memberUpdateId={member.memberUpdate_id} isMember={!!currentMember} callback={() => ConsoleHelper.log('')} />
          )}
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <MemberAccordionWrapper hasError={serverErrors.length > 0}>
                <>
                  {fieldsReady &&
                    fields &&
                    rowsAndColsOrder &&
                    (() => {
                      const groupedFields = new Map<number, { groupName: string; items: FormItemResponse[] }>();

                      // Step 1: Group items by groupID
                      fields.forEach((field) => {
                        const { groupID } = field.formSetting;
                        const groupName = field.formSetting.groupName || 'Unnamed Group';

                        if (!groupedFields.has(groupID)) {
                          groupedFields.set(groupID, { groupName, items: [] });
                        }

                        groupedFields.get(groupID)?.items.push(field);
                      });

                      // Step 2: Render each group
                      return Array.from(groupedFields.entries()).map(([groupID, groupData], index) => {
                        const { groupName, items } = groupData;

                        // Group the items by row and column for rendering
                        const rows = items.reduce((acc, item) => {
                          const { row } = item.formSetting;
                          if (!acc[row]) acc[row] = [];
                          acc[row].push(item);
                          return acc;
                        }, {} as Record<number, FormItemResponse[]>);

                        return (
                          <Accordion key={groupID} disableGutters elevation={0} square defaultExpanded>
                            <AccordionSummary
                              expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
                              aria-controls={`panel-${index}-content`}
                              id={`panel-${index}-header`}
                            >
                              <Typography sx={{ fontWeight: 500 }}>{groupName}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {Object.entries(rows).map(([row, rowItems]) => (
                                <Box sx={{ my: 1, mx: 1, mb: 3 }} key={row}>
                                  <GridWrapper>
                                    {rowItems.map((rowItem) => {
                                      const currentIndex = fields.findIndex(
                                        (y) =>
                                          y.formKey === rowItem.formKey &&
                                          y.formSetting.row === rowItem.formSetting.row &&
                                          y.formSetting.col === rowItem.formSetting.col,
                                      );
                                      return (
                                        <DynamicFormField
                                          key={`${rowItem.formKey}-${rowItem.formSetting.row}-${rowItem.formSetting.col}`}
                                          formItem={rowItem}
                                          index={currentIndex}
                                        />
                                      );
                                    })}
                                  </GridWrapper>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        );
                      });
                    })()}
                </>
              </MemberAccordionWrapper>
            </form>
          </FormProvider>
          {currentUser && (
            <Box
              sx={{
                py: 2,
                boxShadow: '0 20px 15px -20px rgba(0, 0, 0, 0.1) inset !important',
                borderTop: (thm) => `1px solid ${thm.palette.divider}`,
              }}
              ref={profilesTop}
            >
              <MemberProfiles
                member={member}
                callback={() => {
                  callback();
                  scrollToProfilesTop();
                }}
              />
            </Box>
          )}
          <Dialog maxWidth='xs' open={confirmDeleteOpen}>
            <DialogTitle px={2}>{t('member:deleteMember.dialogTitle')}</DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Alert severity='info'>
                <Typography>{t('member:deleteMember.description')}</Typography>
              </Alert>
              {/* <Typography>
                Om en medlem valt att avsluta sitt medlemskap, skall medlemmen i första hand utträdas. Använd då knappen ”utträde av medlem” istället
                för ”radera”. Vill du inte ta bort medlemmen tryck AVBRYT.
              </Typography> */}
              <Divider />
              <Typography p={2}>{t('member:deleteMember.confirm')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button autoFocus variant='outlined' onClick={() => setConfirmDeleteOpen(false)}>
                {t('common:button.cancel')}
              </Button>
              <Button variant='contained' color='primary' disableElevation onClick={() => onDeleteMember()}>
                {t('common:button.ok')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
      dialogActions={
        <Box display='flex' justifyContent='space-between' sx={{ width: '100%' }}>
          {currentUser ? (
            <Button
              variant='outlined'
              color='primary'
              disabled={!getPermission(currentUser, PermissionEnum.MemberDelete)}
              disableElevation
              onClick={() => setConfirmDeleteOpen(true)}
            >
              {t('common:button.delete')}
            </Button>
          ) : (
            <Box />
          )}
          <Box>
            <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 1 }}>
              {t('common:button.cancel')}
            </Button>
            <Button
              autoFocus
              variant='contained'
              color='primary'
              disableElevation
              onClick={onHandleSave}
              disabled={currentMember && member.memberUpdate_id !== null}
            >
              {t('common:button.save')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
